import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  reduce,
  map,
  size,
  toString,
  find,
  filter,
  includes,
  uniq,
  orderBy,
  isEmpty,
} from 'lodash';
import { Box } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import Highcharts from 'highcharts';
import { format } from 'date-fns';

import { RowNode } from 'ag-grid-enterprise';

import {
  ColDef,
  GetMainMenuItemsParams,
  MenuItemDef,
  RowGroupOpenedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';

import Table from 'src/components/shared/Table';

import { truncationValues } from 'src/components/Visualization/visualizationConstants';
import {
  formatParameters,
  IFormatOutputParameters,
} from 'src/components/Visualization/formatParameters';

import { useData } from 'src/hooks/useData';
import OverflowTooltip from 'src/components/shared/OverflowTooltip';

import { IChartData } from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel';

import { ModelInstance, RowIdentifier, Timescale } from 'src/types/models';

// Inlined to avoid back-importing from the forked GenerateChartPanel
// (which would create a circular dep). The v2 fork removed the
// download toolbar, so these methods aren't actually invoked anymore
// — kept on the ref shape for forwardRef compatibility.
export interface OutputsTableRef {
  exportToExcel: () => void;
  exportToCSV: () => void;
}

// Rows shown before the table stops growing and scrolls instead (ruled 2026-09-07, Analyst + Exec alike).
const MAX_AUTO_ROWS = 12;
// Header 28 + MAX_AUTO_ROWS data rows at 28 + the horizontal scrollbar (~18): the viewport held above the cap.
const CAPPED_VIEWPORT_HEIGHT = 28 + MAX_AUTO_ROWS * 28 + 18;

interface IOutputsTableProps {
  timescale: Timescale;
  chartDateType: string;
  chartData: IChartData;
  formatKey: number;
  title: string;
  prefix: string;
  suffix: string;
  decimalPlaces: number;
  magnitudeTruncation: number;
  outputId?: RowIdentifier['id'];
  modelInstanceId?: ModelInstance['id'];
  showScenarios: boolean;
}

const OutputsTable = forwardRef<OutputsTableRef, IOutputsTableProps>(
  (
    {
      chartData,
      formatKey,
      title,
      prefix,
      suffix,
      magnitudeTruncation,
      decimalPlaces,
      outputId,
      modelInstanceId,
      chartDateType,
      timescale,
      showScenarios,
    },
    ref
  ) => {
    const [rowData, setRowData] = useState<any[]>([]);
    const { data } = useData<{
      rowIdentifiers?: RowIdentifier[];
    }>(
      () => ({
        rowIdentifiers:
          modelInstanceId !== undefined
            ? `/instances/${modelInstanceId}/row_identifiers`
            : undefined,
      }),
      []
    );

    const outputs = useMemo(
      () => filter(data.rowIdentifiers, { Type: 'Output' }),
      [modelInstanceId, data]
    );
    const gridRef = useRef<AgGridReact>(null);

    const formatType = useMemo(
      () =>
        (
          find(formatParameters, {
            formatKey,
          }) as IFormatOutputParameters
        ).numberType,
      [formatKey]
    );

    const scenarios = uniq(map(chartData.PublishedData, 'scenario'));

    const autoGroupColumnDef = useMemo<ColDef>(() => {
      return {
        headerName: 'Scenario',
        cellRendererParams: {
          suppressCount: true,
          checkbox: false,
        },
        pinned: 'left',
        cellStyle: (params: any) => {
          // Only apply gray background to top-level scenario rows (level 0)
          if (params.node.group && params.node.level === 0) {
            return {
              fontWeight: 'bold',
              lineHeight: '24px',
              paddingTop: '0px',
              paddingBottom: '0px',
              paddingLeft: '8px',
              paddingRight: '8px',
            };
          }
          return null;
        },
      };
    }, []);

    const [columns, setColumns] = useState<ColDef[]>([]);

    const exportColumns = useMemo(
      () => [
        {
          headerName: 'Segment',
          field: 'segment',
        },
        {
          headerName: 'Data Version',
          field: 'DataVersion',
        },
        ...map(chartData.Dates, (date) => {
          return {
            headerName: includes(
              [Timescale.Weekly, Timescale.Monthly],
              timescale
            )
              ? format(new Date(date.Date), chartDateType)
              : date.Date,
            field: date.Date,
            cellRenderer: (params: ValueFormatterParams) => {
              if (!(params.node as RowNode).group) {
                return `${prefix}${Highcharts.numberFormat(
                  (formatType === 'Percent'
                    ? Number(params.value) * 100
                    : Number(params.value)) /
                    truncationValues[toString(magnitudeTruncation)],
                  decimalPlaces,
                  '.'
                )
                  .toString()
                  .replaceAll(' ', '')
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${suffix}`.replace(
                  /\B(?=(\d{3})+(?!\d))/g,
                  ','
                );
              }
            },
          };
        }),
      ],
      []
    );

    // Update columns when showScenarios or chartData.PreviewData changes
    useEffect(() => {
      const baseColumns = [
        // Conditionally include Version column
        ...(!showScenarios && !isEmpty(chartData.PreviewData)
          ? [
              {
                headerName: 'Version',
                field: 'version',
                pinned: 'left' as const,
                cellRenderer: OverflowTooltip,
              },
            ]
          : []),
        {
          headerName: 'Segment',
          field: 'segment',
          pinned: 'left' as const,
          cellRenderer: OverflowTooltip,
        },
        ...map(chartData.Dates, (date) => {
          return {
            headerName: includes(
              [Timescale.Weekly, Timescale.Monthly],
              timescale
            )
              ? format(new Date(date.Date), chartDateType)
              : date.Date,
            field: date.Date,
            cellRenderer: (params: ValueFormatterParams) => {
              if (!(params.node as RowNode).group) {
                return `${prefix}${Highcharts.numberFormat(
                  (formatType === 'Percent'
                    ? Number(params.value) * 100
                    : Number(params.value)) /
                    truncationValues[toString(magnitudeTruncation)],
                  decimalPlaces,
                  '.'
                )
                  .toString()
                  .replaceAll(' ', '')
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${suffix}`.replace(
                  /\B(?=(\d{3})+(?!\d))/g,
                  ','
                );
              }
            },
          };
        }),
      ];

      // Add scenario grouping column if multiple scenarios
      const updatedColumns =
        scenarios.length > 1
          ? [
              {
                field: 'scenario',
                rowGroup: true,
                hide: true,
                pinned: 'left' as const,
              },
              ...baseColumns,
            ]
          : baseColumns;

      setColumns(updatedColumns);
    }, [
      showScenarios,
      chartData.PreviewData,
      chartData.Dates,
      scenarios.length,
      timescale,
      chartDateType,
      prefix,
      suffix,
      formatType,
      magnitudeTruncation,
      decimalPlaces,
    ]);

    useEffect(() => {
      const publishedData = map(chartData.PublishedData, (series) => ({
        ...series,
        version: 'Original',
      }));
      const previewData = map(chartData.PreviewData, (series) => ({
        ...series,
        version: 'Recalculated',
      }));
      setRowData(
        map([...publishedData, ...previewData], (series) => {
          return {
            version: series.version,
            scenario: series.scenario,
            segment: series.segment,
            ...reduce(
              chartData.Dates,
              (a: Record<string, number>, c, idx) => {
                a[c.Date] = series.values[idx];
                return a;
              },
              {}
            ),
          };
        })
      );
    }, [chartData]);

    const exportRowData = useMemo(() => {
      return orderBy(
        [
          ...map(chartData.PreviewData, (series) => {
            return {
              DataVersion: 'Recalculated',
              id: series.id,
              scenario: series.scenario,
              segment: series.segment,
              ...reduce(
                chartData.Dates,
                (a: Record<string, number>, c, idx) => {
                  a[c.Date] = series.values[idx];
                  return a;
                },
                {}
              ),
            };
          }),
          ...map(chartData.PublishedData, (series) => {
            return {
              DataVersion: 'Original',
              id: series.id,
              scenario: series.scenario,
              segment: series.segment,
              ...reduce(
                chartData.Dates,
                (a: Record<string, number>, c, idx) => {
                  a[c.Date] = series.values[idx];
                  return a;
                },
                {}
              ),
            };
          }),
        ],
        ['segment', 'id']
      );
    }, [chartData]);

    const exportToCSV = (): void => {
      gridRef.current!.api.exportDataAsCsv({
        fileName: `${title}-${format(new Date(), 'MM/dd/yyyy_HH:mm:ss')}`,
      });
    };

    const exportToExcel = (): void => {
      gridRef.current!.api.exportDataAsExcel({
        fileName: `${title}-${format(new Date(), 'MM/dd/yyyy_HH:mm:ss')}`,
        sheetName: find(outputs, {
          id: outputId,
        })?.Name,
        author: 'Trinity LifeSciences',
      });
    };

    useImperativeHandle(ref, () => ({
      exportToCSV,
      exportToExcel,
    }));

    function getMainMenuItems(
      params: GetMainMenuItemsParams
    ): (string | MenuItemDef)[] {
      const menuItems: (MenuItemDef | string)[] = [];
      const itemsToExclude = ['separator', 'pinSubMenu', 'valueAggSubMenu'];
      params.defaultItems.forEach((item) => {
        if (itemsToExclude.indexOf(item) < 0) {
          menuItems.push(item);
        }
      });
      return menuItems;
    }

    const onRowGroupOpened = (params: RowGroupOpenedEvent) => {
      params.columnApi.autoSizeAllColumns();
    };

    const getRowStyle = (params: any): any => {
      // Only apply gray background to top-level scenario rows (level 0)
      if (params.node.group && params.node.level === 0) {
        return {
          backgroundColor: '#f5f5f5',
          fontWeight: 'bold',
        } as React.CSSProperties;
      }
      return undefined;
    };

    const getRowHeight = (params: any) => {
      // v2 fork: tighter row heights for the agentic result card.
      // v1 used 30 / 42; v2 compresses to 24 / 28 to reduce vertical
      // whitespace below the numbers.
      if (params.node.group && params.node.level === 0) {
        return 24;
      }
      return 28;
    };

    // Table height (ruled 2026-09-07): snap to the rows up to MAX_AUTO_ROWS, then hold a fixed viewport and scroll
    // inside. Auto-height alone still left ~100px of blank grid under a 2-row table, because the alpine theme forces
    // min-height:150px on the auto-height body (ag-theme-alpine.css); the override below removes that floor.
    // ag-Grid's own 50px minimum in ag-grid.css still applies.
    const fitsWithoutScroll = size(rowData) <= MAX_AUTO_ROWS;

    return (
      <Box
        sx={{
          // v2 fork: was mt: 2 = 16px in v1; mt: 2 here gives modest
          // breathing room between the chart legend and the table.
          // Header + row heights are passed as AG Grid props below, so
          // CSS variable overrides aren't needed.
          mt: 2,
          '& .ag-theme-alpine, & .ag-theme-material': {
            fontSize: '12px',
          },
          '& .ag-theme-alpine .ag-layout-auto-height .ag-center-cols-viewport, & .ag-theme-alpine .ag-layout-auto-height .ag-center-cols-container':
            {
              minHeight: 'unset',
            },
        }}
      >
        <Box sx={{ display: 'none' }}>
          <Table
            gridRef={gridRef}
            isLoading={false}
            isSuccess
            pagination={false}
            rowCount={size(rowData)}
            columnDefs={exportColumns}
            rowData={exportRowData}
            fitToContent
          />
        </Box>
        <Table
          gridRef={gridRef}
          isLoading={false}
          isSuccess
          pagination={false}
          rowCount={size(rowData)}
          columnDefs={columns}
          rowData={rowData}
          getMainMenuItems={getMainMenuItems}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDisplayType="singleColumn"
          groupDefaultExpanded={-1}
          suppressColumnVirtualisation
          onRowGroupOpened={onRowGroupOpened}
          getRowStyle={getRowStyle}
          getRowHeight={getRowHeight}
          // v2 fork: explicit headerHeight prop wins over the
          // ag-theme-alpine CSS variable default (48px). 28 matches
          // the data-row height from getRowHeight above.
          headerHeight={28}
          // v2 fork: result-card tables shrink to fit their rows
          // instead of reserving the shared Table's default 800px
          // viewport (intended for fixed-page contexts like
          // ScenarioManager). The shared Table's >10-row branch was
          // padding ~500px of whitespace below the data. Above
          // MAX_AUTO_ROWS the table holds CAPPED_VIEWPORT_HEIGHT and
          // scrolls internally instead of growing without bound.
          fitToContent={fitsWithoutScroll}
          viewportHeight={CAPPED_VIEWPORT_HEIGHT}
          defaultColDef={{
            sortable: true,
          }}
          onGridReady={(params) => params.columnApi.autoSizeAllColumns()}
          onFirstDataRendered={(params) => {
            params.columnApi.autoSizeAllColumns();
            // Set default sort by segment after data is loaded
            params.columnApi.applyColumnState({
              state: [
                {
                  colId: 'segment',
                  sort: 'asc',
                },
              ],
            });
          }}
        />
      </Box>
    );
  }
);

export default OutputsTable;
